import { useEffect, useState } from "react";
import RefreshButton from "../../common/Button/RefreshButton";
import ErrorHandler from "../../common/ErrorHandler";
import Filters from "../../common/Filters/Filters";
import SearchBoxWithCustomQuery from "../../common/SearchBoxWithCustomQuery";
import Table from "../../common/Table/Table";
import { formatDateDDMMYYYY } from "../../helpers/functions/dateFormatter";
import { exportToExcel } from "../../helpers/functions/exportToExcel";
import { excelDataFormatter } from "../../helpers/functions/formatters";
import { errorToast } from "../../react-toastfiy/toast";
import auctionService from "../../services/auction/auction.service";
import AddLogModal from "../subscription/AddLogModal";
import ViewLogsModal from "../subscription/ViewLogsModal";

function UserDashboard() {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [addLogModal, setAddLogModal] = useState(false);
  const [viewLogsModal, setViewLogsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [fetchData, setFetchData] = useState(true);

  const filtersList = [
    { key: "user__state__name", name: "State", id: "user__state__name" },
    {
      key: "plan__plan_name",
      name: "Plan",
      id: "plan__plan_name",
    },
  ];

  // Excel columns configuration
  const excelColumns = [
    { label: "Sr No", key: "sr_no", fieldType: "text" },
    { label: "Name", key: "user__name", fieldType: "text" },
    { label: "State", key: "user__state__name", fieldType: "text" },
    { label: "Phone", key: "user__phone", fieldType: "text" },
    {
      label: "Subscription Start Date",
      key: "start_time__date",
      fieldType: "date",
    },
    { label: "End Date", key: "end_time__date", fieldType: "date" },
    { label: "Is Franchised", key: "is_franchised", fieldType: "boolean" },
    { label: "Subscription Count", key: "sub_count", fieldType: "text" },
    {
      label: "Days After Subscription",
      key: "days_since_last_sub",
      fieldType: "text",
    },
    { label: "Approvals", key: "approvals", fieldType: "text" },
    { label: "Assets Bidded", key: "assets_bidded", fieldType: "text" },
    { label: "Payment Done", key: "payments_made", fieldType: "text" },
    { label: "User Added By", key: "user_added_by", fieldType: "text" },
    { label: "Is TA User", key: "is_channel_financed", fieldType: "text" },
  ];

  useEffect(() => {
    try {
      if (fetchData) {
        setFetchData(false);
        setLoadingData(true);
        auctionService.getExpiredSubAnalysys().then((res) => {
          setLoadingData(false);
          if (res?.status === "200") {
            setFilteredData(res.data);
            setAllData(res.data);
          } else {
            errorToast(res?.msg);
          }
        });
      }
    } catch (error) {
      setLoadingData(false);
      console.error(error);
      errorToast(error?.message);
    }
  }, [fetchData]);

  const table_columns = [
    {
      title: "#",
      data_key: "",
      render: (row, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: "User Details",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <div className="d-flex align-items-start">
            {row?.is_franchised === "True" && (
              <span style={{ fontWeight: "bolder" }}>F</span>
            )}
            <div style={{ flexGrow: 1, textAlign: "start" }}>
              <span className="text-nowrap" style={{ fontWeight: "500" }}>
                {row?.user__name}
                {row?.is_channel_financed === "Yes" && (
              <span className=" badge fw-semibold bg-primary-subtle text-primary ms-1">
                TA
              </span>
            )}
              </span>
              <br />
              {row?.user__state__name} - {row?.user__phone}
            </div>
          </div>
        );
      },
    },
    {
      title: "User Added By",
      data_key: "user_added_by",
      render: (row) => {
        return (
          <>
            <span className="text-nowrap d-block">
              {row?.user_added_by}
            </span>
          </>
        );
      },
    },
    {
      title: "Last Subscription End Date",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <span style={{ cursor: "pointer" }} className=" text-nowrap">
              {formatDateDDMMYYYY(new Date(row?.end_time__date))}
            </span>{" "}
            <br />
            <span className=" text-primary bg-primary-subtle badge text-sm">
              {row?.plan__plan_name}
            </span>
          </>
        );
      },
    },
    {
      title: "Total Subscriptions Taken",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <span style={{ cursor: "pointer" }} className=" text-nowrap">
              {row?.sub_count}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "Approved Assets / Participated",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <span style={{ cursor: "pointer" }} className=" text-nowrap">
              {row?.approvals}/{row?.assets_bidded}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "Payment Done / Approved",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <span style={{ cursor: "pointer" }} className=" text-nowrap">
              {row?.payments_made}/{row?.approvals}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "Days Since Subscription End Date",
      data_key: "days_since_last_sub",
      sortable: true,
      render: (row) => {
        return (
          <>
            <span style={{ cursor: "pointer" }} className=" text-nowrap">
              {row?.days_since_last_sub}
            </span>{" "}
          </>
        );
      },
    },

    {
      title: "View Logs",
      data_key: "comment_count",
      sortable: true,
      render: (row) => {
        return (
          <>
            <div
              onClick={() => {
                setSelectedUser({
                  ...row,
                  user_phone: row.user__phone,
                  name: row?.user__name,
                });
                setViewLogsModal(true);
              }}
            >
              <i className="bi bi-eye-fill text-primary fs-6 cursor-pointer me-1 col-6"></i>
              <br />{" "}
              <span style={{ cursor: "pointer" }} className=" text-nowrap">
                {row?.comment_count} comments
              </span>{" "}
            </div>
          </>
        );
      },
    },
    {
      title: "Add Log",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <i
              className="bi bi-plus-circle-fill text-primary fs-6 cursor-pointer me-1 col-6"
              onClick={() => {
                setSelectedUser({
                  ...row,
                  user_phone: row.user__phone,
                  name: row?.user__name,
                });
                setAddLogModal(true);
              }}
            ></i>
          </>
        );
      },
    },
  ];

  try {
    return (
      <>
        {addLogModal && (
          <AddLogModal
            show={addLogModal}
            user={selectedUser}
            onHide={() => {
              setAddLogModal(false);
            }}
          />
        )}
        {viewLogsModal && (
          <ViewLogsModal
            show={viewLogsModal}
            user={selectedUser}
            onHide={() => {
              setViewLogsModal(false);
            }}
          />
        )}

        <div className=" mt-2">
          <div className="p-3 mb-5 rounded mt-4">
            <div className="mb-2">
              <div className="fs-4">Subscription Expiry Analysis</div>

              <div className="d-flex justify-content-end align-items-end flex-wrap gap-2">
                <SearchBoxWithCustomQuery
                  allData={allData}
                  setFilteredData={setFilteredData}
                  placeholder="Search..."
                  return_array={true}
                  keysToSearchOn={[
                    "user__name",
                    "user__state__name",
                    "user__phone",
                  ]}
                  useSearchBoxButton={true}
                />

                <Filters
                  data={allData}
                  setData={setFilteredData}
                  filteredData={filteredData}
                  setFilteredData={setFilteredData}
                  filtersList={filtersList}
                />

                {filteredData && filteredData.length > 0 && (
                  <button
                    className="btn bg-danger-subtle text-danger rounded-circle"
                    onClick={() => {
                      const formattedData = excelDataFormatter(
                        excelColumns,
                        filteredData
                      );
                      exportToExcel(
                        formattedData,
                        `User_Subscription_Expiry_Analysis`
                      );
                    }}
                  >
                    <i className="bi bi-download"></i>
                  </button>
                )}

                <RefreshButton
                  loadingData={loadingData}
                  onClick={() => {
                    setFetchData(true);
                  }}
                />
              </div>
            </div>

            {/* <div className="table-responsive m-0" style={{ maxHeight: "80vh" }}> */}
            <Table
              data={filteredData}
              columns={table_columns.filter((column) => column)}
              loading={loadingData}
              loading_cols={table_columns.filter((column) => column).length}
              loading_rows={10}
              no_data_msg="No Data Found"
              td_class="text-capitalize"
              td_style={{ fontSize: "0.9rem" }}
              th_text_position="start"
              td_text_position="start"
              enablePagination={true}
              maxTableHeight={"60vh"}
            />
            {/* </div> */}
          </div>
        </div>
      </>
    );
  } catch (error) {
    console.log(error);
    return <ErrorHandler error={error} />;
  }
}

export default UserDashboard;
